import * as d3 from 'd3';
import { ScaleLinear } from 'd3';
import React, { useId, useMemo, useRef } from 'react';

import { DataPoint } from '../../PriceChangesChart.types';

export interface PriceChangesChartLinePlotProps {
  data: DataPoint[];
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
  color: string;
  dashed?: boolean;
  tooltipDate?: Date;
  dateToIndexMap?: Map<number, number>;
  threshold?: number;
  positiveColor?: string;
  negativeColor?: string;
  tooltipXPos?: number;
  grayColor?: string;
}

const linePath =
  (
    xScale: ScaleLinear<number, number>,
    yScale: ScaleLinear<number, number>,
    dateToIndexMap?: Map<number, number>
  ) =>
  (ts: DataPoint[]) =>
    d3
      .line<DataPoint>()
      .defined(d => d.value !== undefined)
      .x(d => {
        if (!dateToIndexMap) {
          return xScale(d.date.getTime());
        }

        const index = dateToIndexMap.get(d.date.getTime());
        return index !== undefined ? xScale(index) : 0;
      })
      .y(d => (d.value !== undefined ? yScale(d.value) : 0))
      .curve(d3.curveLinear)(ts);

export const PriceChangesChartLinePlot = ({
  data,
  xScale,
  yScale,
  color,
  dashed,
  tooltipDate,
  dateToIndexMap,
  threshold,
  positiveColor = 'var(--icon-success)',
  negativeColor = 'var(--icon-critical)',
  tooltipXPos,
  grayColor = 'var(--border-default)',
}: PriceChangesChartLinePlotProps) => {
  const clipId = useId();
  const pathRef = useRef<SVGPathElement>(null);

  const path = useMemo(
    () => linePath(xScale, yScale, dateToIndexMap)(data),
    [data, xScale, yScale, dateToIndexMap]
  );

  const selectedData = data.find(
    d => d.date.valueOf() === tooltipDate?.valueOf()
  );

  const optionalDashProp = dashed ? { strokeDasharray: '2' } : {};
  const chartWidth = xScale.range()[1];
  const chartHeight = yScale.range()[0];

  const lastDataPoint = data.findLast(d => d.value !== undefined);
  const activeValue = selectedData?.value ?? lastDataPoint?.value;
  const isActivePositive =
    activeValue !== undefined && activeValue >= (threshold ?? 0);
  const lineColor =
    threshold != null
      ? isActivePositive
        ? positiveColor
        : negativeColor
      : color;

  const selectedPointCx = (() => {
    if (!selectedData) {
      return 0;
    }

    if (!dateToIndexMap) {
      return xScale(selectedData.date.getTime());
    }

    const index = dateToIndexMap.get(selectedData.date.getTime());
    return index !== undefined ? xScale(index) : 0;
  })();

  return (
    <g>
      <path
        ref={pathRef}
        data-testid="line-plot"
        vectorEffect="non-scaling-stroke"
        d={path ?? ''}
        style={{ stroke: lineColor, strokeWidth: 2 }}
        fill="none"
        {...optionalDashProp}
      />

      {tooltipXPos !== undefined && (
        <>
          <defs>
            <clipPath id={`${clipId}-right`}>
              <rect
                x={tooltipXPos}
                y={0}
                width={chartWidth - tooltipXPos}
                height={chartHeight}
              />
            </clipPath>
          </defs>
          <path
            vectorEffect="non-scaling-stroke"
            d={path ?? ''}
            style={{ stroke: grayColor, strokeWidth: 2 }}
            fill="none"
            clipPath={`url(#${clipId}-right)`}
            {...optionalDashProp}
          />
        </>
      )}

      {selectedData?.value !== undefined && (
        <circle
          cx={selectedPointCx}
          cy={yScale(selectedData.value)}
          r="4"
          fill={lineColor}
          stroke="white"
          data-testid="selected-point"
        />
      )}
    </g>
  );
};
